import React, { useEffect, useState } from "react";
import { Table, Tag, Button, Modal, Typography, Space, Empty, message, Descriptions, Popconfirm } from "antd";
import { ReloadOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, CloudSyncOutlined, FileTextOutlined } from "@ant-design/icons";
import { t } from "i18next";
import ApiService from "../../../core/service/ApiService";

const { Text, Paragraph } = Typography;

export default function Hl7MessagesPreview({ appointmentId }) {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sendingReportId, setSendingReportId] = useState(null);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [teletipStatusLoading, setTeletipStatusLoading] = useState(false);
    const [teletipStatusList, setTeletipStatusList] = useState([]);
    const [teletipStatusModalVisible, setTeletipStatusModalVisible] = useState(false);

    const fetchMessages = async () => {
        if (!appointmentId) return;
        setLoading(true);
        try {
            const res = await ApiService.AppointmentTeletip.GetHl7Messages(appointmentId);
            if (res.success) {
                setMessages(res.data || []);
            }
        } finally {
            setLoading(false);
        }
    };

    const sendReportMessage = async (messageId) => {
        setSendingReportId(messageId);
        try {
            const res = await ApiService.AppointmentTeletip.SendReportMessage(messageId);
            if (res.success) {
                message.success(t('Rapor mesajı başarıyla gönderildi'));
                await fetchMessages();
            }
        } finally {
            setSendingReportId(null);
        }
    };

    const viewMessageDetail = async (record) => {
        const res = await ApiService.AppointmentTeletip.GetHl7Message(record.id);
        if (res.success) {
            setSelectedMessage(res.data);
            setDetailModalVisible(true);
        }
    };

    const fetchTeletipStatus = async () => {
        if (!appointmentId) return;
        setTeletipStatusLoading(true);
        try {
            const res = await ApiService.AppointmentTeletip.GetTeletipOrderStatus(appointmentId);
            if (res.success) {
                setTeletipStatusList(res.data || []);
                setTeletipStatusModalVisible(true);
            }
        } finally {
            setTeletipStatusLoading(false);
        }
    };

    useEffect(() => {
        fetchMessages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appointmentId]);

    const columns = [
        {
            title: t('Operation'),
            dataIndex: 'operationName',
            key: 'operationName',
            width: 180,
        },
        {
            title: t('Accession No'),
            dataIndex: 'accessionNumber',
            key: 'accessionNumber',
            width: 100,
            render: (text) => text || '-'
        },
        {
            title: t('Type'),
            dataIndex: 'typeName',
            key: 'typeName',
            width: 80,
            render: (text, record) => (
                <Tag color={record.type === 1 ? 'blue' : 'green'}>
                    {text}
                </Tag>
            )
        },
        {
            title: t('İstem Durumu'),
            dataIndex: 'success',
            key: 'success',
            width: 100,
            render: (success) => (
                success ? (
                    <Tag icon={<CheckCircleOutlined />} color="success">
                        {t('Gönderildi')}
                    </Tag>
                ) : (
                    <Tag icon={<CloseCircleOutlined />} color="error">
                        {t('Başarısız')}
                    </Tag>
                )
            )
        },
        {
            title: t('Rapor Durumu'),
            key: 'reportStatus',
            width: 120,
            render: (_, record) => {
                if (record.reportSentAt) {
                    return record.reportSuccess ? (
                        <Tag icon={<CheckCircleOutlined />} color="success">
                            {t('Gönderildi')}
                        </Tag>
                    ) : (
                        <Tag icon={<CloseCircleOutlined />} color="error">
                            {t('Başarısız')}
                        </Tag>
                    );
                }
                return (
                    <Tag color="default">
                        {t('Beklemede')}
                    </Tag>
                );
            }
        },
        {
            title: t('Rapor Gönderim'),
            dataIndex: 'reportSentAt',
            key: 'reportSentAt',
            width: 140,
            render: (date) => date ? new Date(date).toLocaleString('tr-TR') : '-'
        },
        {
            title: t('İstem Gönderim'),
            dataIndex: 'sentAt',
            key: 'sentAt',
            width: 140,
            render: (date) => date ? new Date(date).toLocaleString('tr-TR') : '-'
        },
        {
            title: t('Hata'),
            key: 'errors',
            ellipsis: true,
            width: 150,
            render: (_, record) => {
                const error = record.error || record.reportError;
                return error ? (
                    <Text type="danger" ellipsis={{ tooltip: error }}>
                        {error}
                    </Text>
                ) : '-';
            }
        },
        {
            title: t('İşlemler'),
            key: 'actions',
            width: 200,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => viewMessageDetail(record)}
                    >
                        {t('Detay')}
                    </Button>
                    {record.success && (
                        <Popconfirm
                            title={t('Rapor Gönder')}
                            description={t('Rapor mesajını teletipe göndermek istediğinize emin misiniz?')}
                            onConfirm={() => sendReportMessage(record.id)}
                            okText={t('Gönder')}
                            cancelText={t('İptal')}
                        >
                            <Button
                                type="link"
                                size="small"
                                icon={<FileTextOutlined />}
                                loading={sendingReportId === record.id}
                                style={{ color: record.reportSuccess ? '#52c41a' : (record.reportSentAt && !record.reportSuccess) ? '#ff4d4f' : undefined }}
                            >
                                {record.reportSuccess ? t('Tekrar Gönder') : (record.reportSentAt && !record.reportSuccess) ? t('Yeniden Dene') : t('Rapor Gönder')}
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            )
        }
    ];

    return (
        <div>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Space>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={fetchMessages}
                        loading={loading}
                    >
                        {t('Refresh')}
                    </Button>
                    <Button
                        type="primary"
                        icon={<CloudSyncOutlined />}
                        onClick={fetchTeletipStatus}
                        loading={teletipStatusLoading}
                    >
                        {t('Teletip Durumu Sorgula')}
                    </Button>
                </Space>
            </div>

            {messages.length === 0 && !loading ? (
                <Empty description={t('No HL7 messages found')} />
            ) : (
                <Table
                    dataSource={messages}
                    columns={columns}
                    rowKey="id"
                    loading={loading}
                    size="small"
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 1400 }}
                />
            )}

            <Modal
                title={t('HL7 Message Detail')}
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={null}
                width={800}
            >
                {selectedMessage && (
                    <div>
                        <div style={{ marginBottom: 16 }}>
                            <Space>
                                <Text strong>{t('Operation')}:</Text>
                                <Text>{selectedMessage.operationName}</Text>
                            </Space>
                        </div>
                        <div style={{ marginBottom: 16 }}>
                            <Space>
                                <Text strong>{t('Type')}:</Text>
                                <Tag color={selectedMessage.type === 1 ? 'blue' : 'green'}>
                                    {selectedMessage.typeName}
                                </Tag>
                                <Text strong>{t('Status')}:</Text>
                                {selectedMessage.success ? (
                                    <Tag icon={<CheckCircleOutlined />} color="success">{t('Success')}</Tag>
                                ) : (
                                    <Tag icon={<CloseCircleOutlined />} color="error">{t('Failed')}</Tag>
                                )}
                            </Space>
                        </div>

                        <div style={{ marginBottom: 16 }}>
                            <Text strong>{t('HL7 Message')}:</Text>
                            <Paragraph
                                copyable
                                style={{
                                    backgroundColor: '#f5f5f5',
                                    padding: 12,
                                    borderRadius: 4,
                                    fontFamily: 'monospace',
                                    fontSize: 12,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-all',
                                    maxHeight: 200,
                                    overflow: 'auto'
                                }}
                            >
                                {selectedMessage.message}
                            </Paragraph>
                        </div>

                        {selectedMessage.ackMessage && (
                            <div style={{ marginBottom: 16 }}>
                                <Text strong>{t('ACK Response')}:</Text>
                                <Paragraph
                                    copyable
                                    style={{
                                        backgroundColor: '#f6ffed',
                                        padding: 12,
                                        borderRadius: 4,
                                        fontFamily: 'monospace',
                                        fontSize: 12,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all',
                                        maxHeight: 200,
                                        overflow: 'auto'
                                    }}
                                >
                                    {selectedMessage.ackMessage}
                                </Paragraph>
                            </div>
                        )}

                        {selectedMessage.error && (
                            <div style={{ marginBottom: 16 }}>
                                <Text strong type="danger">{t('İstem Hatası')}:</Text>
                                <Paragraph
                                    style={{
                                        backgroundColor: '#fff2f0',
                                        padding: 12,
                                        borderRadius: 4,
                                        border: '1px solid #ffccc7',
                                        color: '#cf1322'
                                    }}
                                >
                                    {selectedMessage.error}
                                </Paragraph>
                            </div>
                        )}

                        {/* Rapor Bölümü */}
                        {selectedMessage.reportMessage ? (
                            <>
                                <div style={{ marginBottom: 16, marginTop: 24, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                                    <Space>
                                        <Text strong style={{ fontSize: 16 }}>{t('Rapor Mesajı Bilgileri')}</Text>
                                        {selectedMessage.reportSuccess ? (
                                            <Tag icon={<CheckCircleOutlined />} color="success">{t('Gönderildi')}</Tag>
                                        ) : selectedMessage.reportSentAt ? (
                                            <Tag icon={<CloseCircleOutlined />} color="error">{t('Başarısız')}</Tag>
                                        ) : null}
                                    </Space>
                                </div>

                                <div style={{ marginBottom: 16 }}>
                                    <Text strong>{t('Rapor HL7 Mesajı')}:</Text>
                                    <Paragraph
                                        copyable
                                        style={{
                                            backgroundColor: '#e6f7ff',
                                            padding: 12,
                                            borderRadius: 4,
                                            fontFamily: 'monospace',
                                            fontSize: 12,
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-all',
                                            maxHeight: 200,
                                            overflow: 'auto'
                                        }}
                                    >
                                        {selectedMessage.reportMessage}
                                    </Paragraph>
                                </div>

                                {selectedMessage.reportAckMessage && (
                                    <div style={{ marginBottom: 16 }}>
                                        <Text strong>{t('Rapor ACK Yanıtı')}:</Text>
                                        <Paragraph
                                            copyable
                                            style={{
                                                backgroundColor: '#f6ffed',
                                                padding: 12,
                                                borderRadius: 4,
                                                fontFamily: 'monospace',
                                                fontSize: 12,
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-all',
                                                maxHeight: 200,
                                                overflow: 'auto'
                                            }}
                                        >
                                            {selectedMessage.reportAckMessage}
                                        </Paragraph>
                                    </div>
                                )}

                                {selectedMessage.reportError && (
                                    <div style={{ marginBottom: 16 }}>
                                        <Text strong type="danger">{t('Rapor Hatası')}:</Text>
                                        <Paragraph
                                            style={{
                                                backgroundColor: '#fff2f0',
                                                padding: 12,
                                                borderRadius: 4,
                                                border: '1px solid #ffccc7',
                                                color: '#cf1322'
                                            }}
                                        >
                                            {selectedMessage.reportError}
                                        </Paragraph>
                                    </div>
                                )}

                                {selectedMessage.reportSentAt && (
                                    <div style={{ marginBottom: 8 }}>
                                        <Text type="secondary">{t('Rapor Gönderim')}: {new Date(selectedMessage.reportSentAt).toLocaleString('tr-TR')}</Text>
                                    </div>
                                )}
                            </>
                        ) : selectedMessage.success && (
                            <div style={{ marginBottom: 16, marginTop: 24, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
                                <Space direction="vertical">
                                    <Text strong style={{ fontSize: 16 }}>{t('Rapor Mesajı')}</Text>
                                    <Text type="secondary">{t('Rapor henüz gönderilmedi')}</Text>
                                    <Popconfirm
                                        title={t('Rapor Gönder')}
                                        description={t('Rapor mesajını teletipe göndermek istediğinize emin misiniz?')}
                                        onConfirm={() => {
                                            sendReportMessage(selectedMessage.id);
                                            setDetailModalVisible(false);
                                        }}
                                        okText={t('Gönder')}
                                        cancelText={t('İptal')}
                                    >
                                        <Button 
                                            type="primary" 
                                            icon={<FileTextOutlined />}
                                            loading={sendingReportId === selectedMessage.id}
                                        >
                                            {t('Rapor Gönder')}
                                        </Button>
                                    </Popconfirm>
                                </Space>
                            </div>
                        )}

                        <div>
                            <Space>
                                <Text type="secondary">{t('Oluşturulma')}: {new Date(selectedMessage.createdDate).toLocaleString('tr-TR')}</Text>
                                {selectedMessage.sentAt && (
                                    <Text type="secondary">{t('İstem Gönderim')}: {new Date(selectedMessage.sentAt).toLocaleString('tr-TR')}</Text>
                                )}
                            </Space>
                        </div>
                    </div>
                )}
            </Modal>

            <Modal
                title={t('Teletip Durum Bilgileri')}
                open={teletipStatusModalVisible}
                onCancel={() => setTeletipStatusModalVisible(false)}
                footer={null}
                width={900}
            >
                {teletipStatusList.length === 0 ? (
                    <Empty description={t('Durum bilgisi bulunamadı')} />
                ) : (
                    teletipStatusList.map((status, index) => (
                        <div key={index} style={{ marginBottom: 24, borderBottom: index < teletipStatusList.length - 1 ? '1px solid #f0f0f0' : 'none', paddingBottom: 16 }}>
                            <Descriptions 
                                title={`Accession No: ${status.accessionNumber}`} 
                                bordered 
                                size="small" 
                                column={2}
                            >
                                <Descriptions.Item label={t('TC Kimlik No')}>{status.citizenId || '-'}</Descriptions.Item>
                                <Descriptions.Item label={t('SUT Kodu')}>{status.sutCode || '-'}</Descriptions.Item>
                                <Descriptions.Item label={t('Teletip Durumu')}>
                                    <Tag color={status.teletipStatusId === 1 ? 'success' : status.teletipStatusId === 2 ? 'processing' : 'default'}>
                                        {status.teletipStatus || '-'}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Medula Durumu')}>
                                    <Tag color={status.medulaStatusId === 1 ? 'success' : status.medulaStatusId === 2 ? 'warning' : 'default'}>
                                        {status.medulaStatus || '-'}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('WADO Durumu')}>
                                    <Tag color={status.wadoStatusId === 1 ? 'success' : status.wadoStatusId === 2 ? 'processing' : status.wadoStatusId === 3 ? 'warning' : 'default'}>
                                        {status.wadoStatus || '-'}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Rapor Durumu')}>
                                    <Tag color={status.reportStatusId === 1 ? 'success' : status.reportStatusId === 2 ? 'warning' : 'default'}>
                                        {status.reportStatus || '-'}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Doz Durumu')}>
                                    <Tag color={status.doseStatusId === 1 ? 'success' : status.doseStatusId === 2 ? 'warning' : 'default'}>
                                        {status.doseStatus || '-'}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Mükerrerlik Durumu')}>
                                    {status.patientHistorySearchStatus || '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('İstem Tarihi')}>
                                    {status.requestDate ? new Date(status.requestDate).toLocaleString('tr-TR') : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Randevu Tarihi')}>
                                    {status.scheduleDate ? new Date(status.scheduleDate).toLocaleString('tr-TR') : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Çekim Tarihi')}>
                                    {status.performedDate ? new Date(status.performedDate).toLocaleString('tr-TR') : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Rapor Onay Tarihi')}>
                                    {status.reportedDate ? new Date(status.reportedDate).toLocaleString('tr-TR') : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Son Medula Gönderim')}>
                                    {status.lastMedulaSendDate ? new Date(status.lastMedulaSendDate).toLocaleString('tr-TR') : '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Medula Yanıt Kodu')}>
                                    {status.medulaResponseCode || '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('WADO Test Sayısı')}>
                                    {status.wadoTestCount ?? '-'}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('Son WADO Test')}>
                                    {status.lastWadoTestDate ? new Date(status.lastWadoTestDate).toLocaleString('tr-TR') : '-'}
                                </Descriptions.Item>
                                {status.medulaResponseMessage && (
                                    <Descriptions.Item label={t('Medula Mesajı')} span={2}>
                                        {status.medulaResponseMessage}
                                    </Descriptions.Item>
                                )}
                                {status.error && (
                                    <Descriptions.Item label={t('Hata')} span={2}>
                                        <Text type="danger">{status.error}</Text>
                                    </Descriptions.Item>
                                )}
                                {status.lastWadoResponseMessage && (
                                    <Descriptions.Item label={t('Son WADO Yanıtı')} span={2}>
                                        {status.lastWadoResponseMessage}
                                    </Descriptions.Item>
                                )}
                            </Descriptions>
                        </div>
                    ))
                )}
            </Modal>
        </div>
    );
}
